// MachineUtilizationDashboard/components/LiveMachineGrid.jsx

import { useMemo, useState } from "react";
import {
  formatNumber,
  getStatusClass,
  getUtilizationLevel,
} from "../utils/machineDashboardUtils";

function LiveMachineGrid({ machines = [] }) {
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(12);

  const filteredMachines = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return (Array.isArray(machines) ? machines : [])
      .filter((machine) => {
        const matchesStatus =
          statusFilter === "ALL" ||
          String(machine?.liveStatus || "").toUpperCase() === statusFilter;

        const searchText = [
          machine?.machineCode,
          machine?.machineName,
          machine?.deptCode,
          machine?.deptName,
          machine?.jobcard,
          machine?.operatorUserName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        const matchesSearch =
          !normalizedSearch || searchText.includes(normalizedSearch);

        return matchesStatus && matchesSearch;
      })
      .sort((a, b) => {
        const deptCompare = String(a?.deptCode || "").localeCompare(
          String(b?.deptCode || ""),
        );

        if (deptCompare !== 0) {
          return deptCompare;
        }

        return String(a?.machineCode || "").localeCompare(
          String(b?.machineCode || ""),
        );
      });
  }, [machines, search, statusFilter]);

  const displayedMachines = filteredMachines.slice(0, visibleCount);

  return (
    <div className="mud-live-machines">
      <div className="mud-live-toolbar">
        <div className="mud-search">
          <i className="bi bi-search" />

          <input
            type="text"
            value={search}
            placeholder="Search machine, operator, job card..."
            onChange={(event) => {
              setSearch(event.target.value);
              setVisibleCount(12);
            }}
          />
        </div>

        <div className="mud-filter-tabs">
          {["ALL", "RUNNING", "IDLE", "STOPPED"].map((status) => (
            <button
              type="button"
              key={status}
              className={statusFilter === status ? "active" : ""}
              onClick={() => {
                setStatusFilter(status);
                setVisibleCount(12);
              }}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {displayedMachines.length === 0 ? (
        <div className="mud-empty-state">
          No machines match the selected filter.
        </div>
      ) : (
        <div className="mud-machine-card-grid">
          {displayedMachines.map((machine, index) => {
            const utilizationStatus = getUtilizationLevel(
              machine?.utilizationPercentage,
            );

            const statusClass = getStatusClass(machine?.liveStatus);

            return (
              <article
                className={`mud-machine-card mud-machine-card--${statusClass}`}
                key={`${machine?.machineCode}-${machine?.deptCode}-${index}`}
              >
                <div className="mud-machine-card__header">
                  <div className="mud-machine-card__machine">
                    <span className="mud-machine-card__icon">
                      <i className="bi bi-cpu-fill" />
                    </span>

                    <div>
                      <strong>{machine?.machineCode || "-"}</strong>
                      <small>{machine?.machineName || "-"}</small>
                    </div>
                  </div>

                  <div className="mud-machine-card__department">
                    <i className="bi bi-building" />

                    <span>
                      {machine?.deptCode || "-"} · {machine?.deptName || "-"}
                    </span>
                  </div>

                  <span className={`mud-live-status ${statusClass}`}>
                    <i />
                    {machine?.liveStatus || "STOPPED"}
                  </span>
                </div>

                <div className="mud-machine-card__utilization">
                  <span>Utilization</span>
                  <div>
                    <strong>
                      {formatNumber(machine?.utilizationPercentage, 2)}%
                    </strong>
                  </div>

                  <span
                    className={`mud-performance-badge mud-performance-badge--${utilizationStatus.tone}`}
                  >
                    {utilizationStatus.label}
                  </span>
                </div>

                <div className="mud-mini-progress">
                  <span
                    style={{
                      width: `${Math.min(
                        Math.max(
                          Number(machine?.utilizationPercentage || 0),
                          0,
                        ),
                        100,
                      )}%`,
                    }}
                  />
                </div>

                <div className="mud-machine-card__metrics">
                  <div>
                    <span>Actual</span>
                    <strong>{machine?.actualDisplay || "0s"}</strong>
                  </div>

                  <div>
                    <span>Target</span>
                    <strong>{machine?.targetDisplay || "0s"}</strong>
                  </div>

                  <div>
                    <span>Production</span>
                    <strong>{formatNumber(machine?.totalProduction)}</strong>
                  </div>

                  <div>
                    <span>Reject</span>
                    <strong className="danger">
                      {formatNumber(machine?.rejectQuantity)}
                    </strong>
                  </div>
                </div>

                <div className="mud-machine-card__footer">
                  <div>
                    <i className="bi bi-person" />

                    <span>{machine?.operatorUserName || "No operator"}</span>
                  </div>

                  {machine?.jobcard && (
                    <span className="mud-jobcard-badge">{machine.jobcard}</span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {visibleCount < filteredMachines.length && (
        <div className="mud-load-more">
          <button
            type="button"
            onClick={() => setVisibleCount((current) => current + 12)}
          >
            Load more machines
            <i className="bi bi-chevron-down" />
          </button>
        </div>
      )}
    </div>
  );
}

export default LiveMachineGrid;
